/**
 * AccordionSection - Collapsible section for admin console
 * Phase 11.5a - Admin Foundations
 */
import React, { useState, useCallback } from 'react'
import { IoChevronDown, IoChevronForward } from 'react-icons/io5'

interface AccordionSectionProps {
  title: string
  badge?: number | string
  badgeVariant?: 'default' | 'warning' | 'error' | 'success'
  defaultOpen?: boolean
  children: React.ReactNode
  className?: string
}

export function AccordionSection({
  title,
  badge,
  badgeVariant = 'default',
  defaultOpen = false,
  children,
  className = '',
}: AccordionSectionProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen)

  const toggle = useCallback(() => {
    setIsOpen((prev) => !prev)
  }, [])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        toggle()
      }
    },
    [toggle]
  )

  return (
    <div className={`admin-accordion ${isOpen ? 'open' : ''} ${className}`}>
      <button
        type="button"
        className="admin-accordion-header"
        onClick={toggle}
        onKeyDown={handleKeyDown}
        aria-expanded={isOpen}
      >
        <span className="admin-accordion-icon">
          {isOpen ? <IoChevronDown size={16} /> : <IoChevronForward size={16} />}
        </span>
        <span className="admin-accordion-title">{title}</span>
        {badge !== undefined && (
          <span className={`admin-accordion-badge ${badgeVariant}`}>{badge}</span>
        )}
      </button>
      <div className="admin-accordion-content" aria-hidden={!isOpen}>
        {isOpen && <div className="admin-accordion-inner">{children}</div>}
      </div>
    </div>
  )
}
